import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import { refreshGlobalSettings } from '../../../../hooks/useSystemSettings';

interface GA4Config {
  ga4_enabled: string;
  ga4_measurement_id: string;
  ga4_environment: string;
  ga4_track_events: string;
}

const EVENTS_CATALOG = [
  { group: 'Autenticação', events: [
    { name: 'login', desc: 'Usuário faz login' },
    { name: 'signup', desc: 'Novo cadastro' },
    { name: 'logout', desc: 'Usuário faz logout' },
  ]},
  { group: 'Onboarding', events: [
    { name: 'onboarding_started', desc: 'Wizard de onboarding iniciado' },
    { name: 'onboarding_completed', desc: 'Onboarding concluído' },
  ]},
  { group: 'Financeiro', events: [
    { name: 'transaction_created', desc: 'Novo lançamento criado (type, category, amount_range)' },
    { name: 'transaction_updated', desc: 'Lançamento editado' },
    { name: 'transaction_deleted', desc: 'Lançamento removido' },
    { name: 'transaction_marked_paid', desc: 'Marcado como pago/recebido' },
  ]},
  { group: 'Uso do Sistema', events: [
    { name: 'account_created', desc: 'Nova conta bancária criada' },
    { name: 'card_created', desc: 'Novo cartão criado' },
    { name: 'goal_created', desc: 'Nova meta criada' },
    { name: 'report_viewed', desc: 'Relatório visualizado (tab)' },
    { name: 'payee_created', desc: 'Novo favorecido criado' },
    { name: 'payee_used', desc: 'Favorecido selecionado em lançamento' },
  ]},
  { group: 'Monetização', events: [
    { name: 'plan_viewed', desc: 'Tela de planos visualizada' },
    { name: 'upgrade_clicked', desc: 'Botão de upgrade clicado' },
    { name: 'checkout_started', desc: 'Checkout aberto' },
    { name: 'payment_success', desc: 'Pagamento aprovado' },
    { name: 'payment_failed', desc: 'Pagamento falhou/recusado' },
  ]},
];

const DEFAULT: GA4Config = {
  ga4_enabled: 'false',
  ga4_measurement_id: '',
  ga4_environment: 'production',
  ga4_track_events: 'true',
};

export const AnalyticsIntegration = () => {
  const [config, setConfig] = useState<GA4Config>(DEFAULT);
  const [editing, setEditing] = useState<GA4Config>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showEventsCatalog, setShowEventsCatalog] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('system_settings')
      .select('key, value')
      .in('key', ['ga4_enabled', 'ga4_measurement_id', 'ga4_environment', 'ga4_track_events']);
    if (data) {
      const map: Partial<GA4Config> = {};
      data.forEach((row: { key: string; value: string }) => {
        (map as Record<string, string>)[row.key] = row.value ?? '';
      });
      const merged = { ...DEFAULT, ...map };
      setConfig(merged);
      setEditing(merged);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    const rows = Object.entries(editing).map(([key, value]) => ({ key, value: String(value) }));
    await supabase.from('system_settings').upsert(rows, { onConflict: 'key' });
    await refreshGlobalSettings();
    setConfig({ ...editing });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const hasChanges = JSON.stringify(editing) !== JSON.stringify(config);

  const isEnabled = editing.ga4_enabled === 'true';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line text-3xl text-violet-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status card */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`border rounded-2xl p-5 flex items-center gap-3 ${isEnabled ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-white/10 bg-white/5'}`}>
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isEnabled ? 'bg-emerald-500/20' : 'bg-white/10'}`}>
            <i className={`ri-bar-chart-2-line text-[18px] ${isEnabled ? 'text-emerald-400' : 'text-white/30'}`} />
          </div>
          <div>
            <p className="text-[11px] text-white/30">Status</p>
            <p className={`text-[15px] font-700 ${isEnabled ? 'text-emerald-300' : 'text-white/50'}`}>
              {isEnabled ? 'Ativo' : 'Inativo'}
            </p>
          </div>
        </div>
        <div className="border border-white/10 bg-white/5 rounded-2xl p-5 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
            <i className="ri-fingerprint-line text-[18px] text-white/30" />
          </div>
          <div>
            <p className="text-[11px] text-white/30">Measurement ID</p>
            <p className="text-[13px] font-600 text-white/70 font-mono">
              {editing.ga4_measurement_id || '—'}
            </p>
          </div>
        </div>
        <div className="border border-white/10 bg-white/5 rounded-2xl p-5 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
            <i className="ri-global-line text-[18px] text-white/30" />
          </div>
          <div>
            <p className="text-[11px] text-white/30">Ambiente</p>
            <p className="text-[15px] font-700 text-white/70 capitalize">{editing.ga4_environment}</p>
          </div>
        </div>
      </div>

      {/* Main config panel */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-orange-500/20 rounded-xl">
              <i className="ri-bar-chart-grouped-line text-orange-400 text-[18px]" />
            </div>
            <div>
              <h2 className="text-[15px] font-700 text-white">Google Analytics 4</h2>
              <p className="text-[11px] text-white/30">Rastreamento de uso e eventos estratégicos</p>
            </div>
          </div>
          {saved && (
            <span className="flex items-center gap-1.5 text-[12px] font-600 text-emerald-400">
              <i className="ri-checkbox-circle-line" />Salvo com sucesso
            </span>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Enable toggle */}
          <div className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="flex items-center gap-3">
              <i className="ri-toggle-line text-[20px] text-violet-400" />
              <div>
                <p className="text-[14px] font-700 text-white">Ativar Google Analytics</p>
                <p className="text-[11px] text-white/40 mt-0.5">
                  Carrega o script GA4 e habilita o rastreamento de pageviews e eventos
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEditing(p => ({ ...p, ga4_enabled: p.ga4_enabled === 'true' ? 'false' : 'true' }))}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 overflow-hidden ${isEnabled ? 'bg-violet-600' : 'bg-white/10'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${isEnabled ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {/* Measurement ID */}
          <div>
            <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-1.5 block">
              Measurement ID
            </label>
            <input
              type="text"
              value={editing.ga4_measurement_id}
              onChange={e => setEditing(p => ({ ...p, ga4_measurement_id: e.target.value.trim() }))}
              placeholder="G-XXXXXXXXXX"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-[13px] font-mono outline-none focus:border-violet-500 placeholder:text-white/20"
            />
            <p className="text-[11px] text-white/25 mt-1.5">
              Encontre em <strong className="text-white/40">GA4 → Admin → Data Streams → Web → Measurement ID</strong>. Formato: G-XXXXXXXXXX
            </p>
          </div>

          {/* Environment */}
          <div>
            <label className="text-[11px] text-white/40 uppercase tracking-wider font-600 mb-2 block">
              Ambiente de carregamento
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'production', label: 'Somente produção', desc: 'Não carrega em localhost', icon: 'ri-rocket-line' },
                { value: 'all', label: 'Todos os ambientes', desc: 'Inclui localhost (dev)', icon: 'ri-code-s-slash-line' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setEditing(p => ({ ...p, ga4_environment: opt.value }))}
                  className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all cursor-pointer ${
                    editing.ga4_environment === opt.value
                      ? 'border-violet-500/50 bg-violet-500/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <i className={`${opt.icon} text-[18px] mt-0.5 ${editing.ga4_environment === opt.value ? 'text-violet-400' : 'text-white/30'}`} />
                  <div>
                    <p className={`text-[13px] font-700 ${editing.ga4_environment === opt.value ? 'text-violet-300' : 'text-white/70'}`}>
                      {opt.label}
                    </p>
                    <p className="text-[11px] text-white/30 mt-0.5">{opt.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Track events toggle */}
          <div className="flex items-center justify-between py-3 px-4 bg-white/5 rounded-2xl border border-white/5">
            <div className="flex items-center gap-3">
              <i className="ri-cursor-line text-[18px] text-sky-400" />
              <div>
                <p className="text-[14px] font-700 text-white">Rastrear eventos de interação</p>
                <p className="text-[11px] text-white/40 mt-0.5">
                  login, transaction_created, goal_created, upgrade_clicked, etc.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEditing(p => ({ ...p, ga4_track_events: p.ga4_track_events === 'true' ? 'false' : 'true' }))}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 overflow-hidden ${editing.ga4_track_events === 'true' ? 'bg-sky-600' : 'bg-white/10'}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${editing.ga4_track_events === 'true' ? 'left-6' : 'left-1'}`} />
            </button>
          </div>

          {/* Privacy note */}
          <div className="flex items-start gap-3 px-4 py-3.5 bg-amber-500/10 border border-amber-500/20 rounded-2xl">
            <i className="ri-shield-user-line text-amber-400 text-[18px] shrink-0 mt-0.5" />
            <div>
              <p className="text-[13px] font-700 text-amber-300">Privacidade garantida</p>
              <p className="text-[12px] text-white/50 mt-0.5 leading-relaxed">
                Nenhum dado sensível é enviado: CPF, CNPJ, e-mails, nomes e valores exatos permanecem apenas no sistema. Apenas metadados de uso (tipo de evento, plano do usuário, timestamp) são enviados ao GA4.
              </p>
            </div>
          </div>

          {/* Quick setup guide */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
            <p className="text-[12px] font-700 text-white/50 uppercase tracking-wider mb-3">
              Como configurar em 3 passos
            </p>
            <ol className="space-y-3">
              {[
                { step: '1', text: 'Acesse analytics.google.com → Criar propriedade → Web', link: 'https://analytics.google.com' },
                { step: '2', text: 'Em Admin → Data Streams → Web, copie o Measurement ID (G-XXXXXXXX)' },
                { step: '3', text: 'Cole o ID acima, ative o toggle e salve. O GA carrega automaticamente.' },
              ].map(item => (
                <li key={item.step} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-violet-500/30 text-violet-300 text-[11px] font-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {item.step}
                  </span>
                  <p className="text-[12px] text-white/50 leading-relaxed">
                    {item.text}
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer"
                        className="ml-1 text-violet-300 hover:text-violet-200 underline">
                        {item.link}
                      </a>
                    )}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          {/* Save button */}
          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <p className="text-[11px] text-white/25">
              As alterações refletem no próximo carregamento do app
            </p>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="flex items-center gap-2 px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white text-[13px] font-700 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              {saving && <i className="ri-loader-4-line animate-spin" />}
              {saving ? 'Salvando...' : 'Salvar configurações'}
            </button>
          </div>
        </div>
      </div>

      {/* Events catalog */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <button
          type="button"
          onClick={() => setShowEventsCatalog(v => !v)}
          className="w-full flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <i className="ri-list-check-3 text-[18px] text-white/40" />
            <div className="text-left">
              <p className="text-[14px] font-700 text-white">Catálogo de eventos rastreados</p>
              <p className="text-[11px] text-white/30">Veja todos os eventos que o sistema envia ao GA4</p>
            </div>
          </div>
          <i className={`ri-arrow-${showEventsCatalog ? 'up' : 'down'}-s-line text-white/30 text-[18px]`} />
        </button>

        {showEventsCatalog && (
          <div className="border-t border-white/10 px-6 py-5 grid grid-cols-2 gap-6">
            {EVENTS_CATALOG.map(group => (
              <div key={group.group}>
                <p className="text-[11px] font-700 text-white/40 uppercase tracking-wider mb-3">{group.group}</p>
                <div className="space-y-2">
                  {group.events.map(ev => (
                    <div key={ev.name} className="flex items-start gap-2.5">
                      <span className="px-2 py-0.5 bg-white/5 border border-white/10 rounded-lg text-[11px] font-mono text-violet-300 flex-shrink-0">
                        {ev.name}
                      </span>
                      <p className="text-[11px] text-white/40 leading-tight pt-0.5">{ev.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
